/**
 * User-facing copy for add_document failures.
 *
 * The append is asynchronous, so errors arrive from two places with the same shape: a synchronous
 * 422 on the PUT (bad file type, over the cap, nothing selected) and the polled job result (parse
 * failure, the under-lock count race, an unexpected server failure). Both surface as an `err_*`
 * code, so one mapper covers them.
 */
export const ADD_DOCUMENT_ERROR_COPY: Record<string, string> = {
  err_multi_doc_not_enabled:
    "Multiple offer documents aren't enabled for your account.",
  err_offer_template_missing: 'This offer has no template to add documents to.',
  err_document_parse_failed:
    "We couldn't read that .docx. Re-save it and try again.",
  err_too_many_offer_documents: 'An offer can have at most 10 documents.',
  err_invalid_file_type: 'Only .docx files can be added as offer documents.',
  err_file_size_too_large: 'That file is too large.',
  err_attachments_required: 'Select at least one file.',
  // The append runs in a worker; until it commits, the offer is blocked from sends and further
  // document edits so an envelope can't be built from the about-to-change document set.
  err_document_append_in_progress:
    'Still adding your last document. Try again in a moment.',
  err_ats_offer_not_found: 'This offer no longer exists.',
  err_document_append_timeout:
    'This is taking longer than expected. Refresh to see whether the documents were added.',
  err_poll_result_not_found:
    'We lost track of this upload. Refresh to see whether the documents were added.',
};

const FALLBACK = 'Something went wrong. Please try again.';

export interface AddDocumentError {
  message?: string;
  response?: { data?: { errors?: unknown[] } };
}

/**
 * The code arrives in one of two places. A synchronous 422 rejects with the raw axios error, whose
 * body carries `errors: ['err_...']` — `message` there is axios' own "Request failed with status
 * code 422". A polled failure is thrown by addOfferDocumentAndWait as an Error whose message IS the
 * code. Body first, so the axios text never wins over a real code.
 */
export const addDocumentErrorCode = (
  error?: AddDocumentError | null
): string | undefined => {
  const fromBody = error?.response?.data?.errors?.[0];
  if (typeof fromBody === 'string') return fromBody;
  return error?.message;
};

/**
 * Resolves an error thrown by the add-document mutation to display copy.
 *
 * Unrecognised values fall back to generic copy rather than being shown raw: the server only emits
 * `err_*` codes here (raw exception strings are logged, never returned), but an axios/network error
 * reaching this path would otherwise leak an internal message into a toast.
 */
export const addDocumentErrorCopy = (
  error?: AddDocumentError | null
): string => {
  const code = addDocumentErrorCode(error);
  if (!code) return FALLBACK;
  return ADD_DOCUMENT_ERROR_COPY[code] ?? FALLBACK;
};
